import { useState, useCallback, useRef } from 'react';
import { AudioRecorder } from '../utils/audioRecorder';

const MAX_RECORDING_DURATION = 60000; // 最大录制时长 60秒

export interface UseAudioRecorderReturn {
  isRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  error: string | null;
  recordingDuration: number; // 当前录制时长（毫秒）
  isMaxDurationReached: boolean; // 是否达到最大录制时长
  maxRecordingDuration: number; // 最大录制时长（毫秒）
}

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isMaxDurationReached, setIsMaxDurationReached] = useState(false);
  const recorderRef = useRef<AudioRecorder | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setRecordingDuration(0);
      recorderRef.current = new AudioRecorder({
        sampleRate: 16000,
        channelCount: 1,
      });
      await recorderRef.current.start();
      setIsRecording(true);
      startTimeRef.current = Date.now();

      // 启动计时器更新录制时长
      timerRef.current = setInterval(() => {
        const duration = Date.now() - startTimeRef.current;
        setRecordingDuration(duration);
        setIsMaxDurationReached(duration >= MAX_RECORDING_DURATION);
      }, 100);
    } catch (err) {
      setError('无法访问麦克风，请检查权限设置');
      throw err;
    }
  }, []);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    // 清理计时器
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!recorderRef.current) {
      return null;
    }

    try {
      const wavBlob = await recorderRef.current.stop();
      setIsRecording(false);
      setRecordingDuration(0);
      setIsMaxDurationReached(false);
      return wavBlob;
    } catch (err) {
      setError('停止录制失败');
      setIsRecording(false);
      setRecordingDuration(0);
      setIsMaxDurationReached(false);
      return null;
    }
  }, []);

  return {
    isRecording,
    startRecording,
    stopRecording,
    error,
    recordingDuration,
    isMaxDurationReached,
    maxRecordingDuration: MAX_RECORDING_DURATION,
  };
}
